import React from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  // Mock data for charts and KPIs
  const revenueData = [{
    month: 'Jan',
    revenue: 4200
  }, {
    month: 'Feb',
    revenue: 5800
  }, {
    month: 'Mar',
    revenue: 7300
  }, {
    month: 'Apr',
    revenue: 6500
  }, {
    month: 'May',
    revenue: 8900
  }, {
    month: 'Jun',
    revenue: 10200
  }, {
    month: 'Jul',
    revenue: 11500
  }, {
    month: 'Aug',
    revenue: 10800
  }, {
    month: 'Sep',
    revenue: 12300
  }, {
    month: 'Oct',
    revenue: 11700
  }, {
    month: 'Nov',
    revenue: 13200
  }, {
    month: 'Dec',
    revenue: shop.revenue.total
  }];
  const ordersData = [{
    month: 'Jan',
    orders: 32
  }, {
    month: 'Feb',
    orders: 45
  }, {
    month: 'Mar',
    orders: 51
  }, {
    month: 'Apr',
    orders: 48
  }, {
    month: 'May',
    orders: 62
  }, {
    month: 'Jun',
    orders: 76
  }, {
    month: 'Jul',
    orders: 85
  }, {
    month: 'Aug',
    orders: 79
  }, {
    month: 'Sep',
    orders: 94
  }, {
    month: 'Oct',
    orders: 88
  }, {
    month: 'Nov',
    orders: 102
  }, {
    month: 'Dec',
    orders: shop.ordersCount
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-1145">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1146">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1147">
          <BarChartIcon size={18} className="mr-2 text-blue-600" data-id="element-1148" />
          Shop Dashboard
        </h2>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1149">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1150">
            <div className="flex justify-between items-start" data-id="element-1151">
              <div data-id="element-1152">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1153">Total Revenue</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-1154">
                  ${shop.revenue.total.toLocaleString()}
                </h3>
              </div>
              <div className="p-2 bg-blue-50 rounded-md" data-id="element-1155">
                <DollarSignIcon size={20} className="text-blue-600" data-id="element-1156" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-1157">
              <span className={`inline-flex items-center text-xs font-medium ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1158">
                {shop.revenue.growth >= 0 ? <ArrowUpIcon size={12} className="mr-1" data-id="element-1159" /> : <ArrowDownIcon size={12} className="mr-1" data-id="element-1160" />}
                {Math.abs(shop.revenue.growth)}%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-1161">vs last month</span>
            </div>
          </div>
          {/* Orders KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1162">
            <div className="flex justify-between items-start" data-id="element-1163">
              <div data-id="element-1164">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1165">Total Orders</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-1166">
                  {shop.ordersCount}
                </h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-md" data-id="element-1167">
                <ShoppingCartIcon size={20} className="text-purple-600" data-id="element-1168" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-1169">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-1170">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-1171" />
                12.4%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-1172">vs last month</span>
            </div>
          </div>
          {/* Products KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1173">
            <div className="flex justify-between items-start" data-id="element-1174">
              <div data-id="element-1175">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1176">Total Products</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-1177">
                  {shop.productsCount}
                </h3>
              </div>
              <div className="p-2 bg-green-50 rounded-md" data-id="element-1178">
                <PackageIcon size={20} className="text-green-600" data-id="element-1179" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-1180">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-1181">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-1182" />
                5.2%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-1183">vs last month</span>
            </div>
          </div>
          {/* Customers KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1184">
            <div className="flex justify-between items-start" data-id="element-1185">
              <div data-id="element-1186">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1187">Total Customers</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-1188">
                  {customerStats.total}
                </h3>
              </div>
              <div className="p-2 bg-orange-50 rounded-md" data-id="element-1189">
                <UsersIcon size={20} className="text-orange-600" data-id="element-1190" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-1191">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-1192">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-1193" />
                8.7%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-1194">vs last month</span>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-1195">
          {/* Revenue Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1196">
            <div className="flex justify-between items-center mb-4" data-id="element-1197">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-1198">
                Revenue Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-1199">
                <CalendarIcon size={14} className="mr-1" data-id="element-1200" />
                Last 12 months
              </div>
            </div>
            <div className="h-60 w-full" data-id="element-1201">
              <div className="flex h-full items-end" data-id="element-1202">
                {revenueData.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center" data-id="element-1203">
                    <div className="w-full mx-1 bg-blue-500 rounded-t-sm hover:bg-blue-600 transition-colors" style={{
                  height: `${item.revenue / maxRevenue * 100}%`,
                  minHeight: '4px'
                }} data-id="element-1204"></div>
                    <span className="text-xs text-gray-500 mt-1" data-id="element-1205">
                      {item.month}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Orders Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1206">
            <div className="flex justify-between items-center mb-4" data-id="element-1207">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-1208">
                Orders Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-1209">
                <CalendarIcon size={14} className="mr-1" data-id="element-1210" />
                Last 12 months
              </div>
            </div>
            <div className="h-60 w-full" data-id="element-1211">
              <div className="flex h-full items-end" data-id="element-1212">
                {ordersData.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center" data-id="element-1213">
                    <div className="w-full mx-1 bg-purple-500 rounded-t-sm hover:bg-purple-600 transition-colors" style={{
                  height: `${item.orders / maxOrders * 100}%`,
                  minHeight: '4px'
                }} data-id="element-1214"></div>
                    <span className="text-xs text-gray-500 mt-1" data-id="element-1215">
                      {item.month}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        {/* Additional Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1216">
          {/* Top Products */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1217">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1218">
              Top Selling Products
            </h4>
            <div className="space-y-3" data-id="element-1219">
              {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-1220">
                  <div className="flex items-center" data-id="element-1221">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 mr-3" data-id="element-1222">
                      {index + 1}
                    </div>
                    <div data-id="element-1223">
                      <p className="text-sm font-medium text-gray-800" data-id="element-1224">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1225">
                        {product.sales} sold
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1226">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1227">
                      ${product.revenue.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1228">Revenue</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Customer Insights */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1229">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1230">
              Customer Insights
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-1231">
              <div className="bg-gray-50 rounded-md p-3" data-id="element-1232">
                <p className="text-xs text-gray-500 mb-1" data-id="element-1233">New Customers</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-1234">
                  {customerStats.new}
                </p>
                <div className="mt-1 flex items-center" data-id="element-1235">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-1236">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-1237" />
                    14.3%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-1238">
                <p className="text-xs text-gray-500 mb-1" data-id="element-1239">
                  Returning Customers
                </p>
                <p className="text-lg font-medium text-gray-800" data-id="element-1240">
                  {customerStats.returning}
                </p>
                <div className="mt-1 flex items-center" data-id="element-1241">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-1242">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-1243" />
                    7.2%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-1244">
                <p className="text-xs text-gray-500 mb-1" data-id="element-1245">Avg. Order Value</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-1246">
                  ${(shop.revenue.total / shop.ordersCount).toFixed(2)}
                </p>
                <div className="mt-1 flex items-center" data-id="element-1247">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-1248">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-1249" />
                    3.5%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-1250">
                <p className="text-xs text-gray-500 mb-1" data-id="element-1251">Conversion Rate</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-1252">
                  {customerStats.conversionRate}%
                </p>
                <div className="mt-1 flex items-center" data-id="element-1253">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-1254">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-1255" />
                    1.2%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}